import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

export default function PublicNavbar() {
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const links = [
    { to: '/features', label: t('publicNav.features', 'Features') },
    { to: '/pricing', label: t('publicNav.pricing', 'Pricing') },
    { to: '/guide', label: t('nav.guide', 'Guide') },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="bg-white border-b border-gray-100 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 flex items-center justify-between h-16">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3">
          <img src="/logo.png" alt="BuildMyShifts" className="w-10 h-10 object-contain" />
          <span className="text-xl font-bold text-gray-900">
            Build My <span className="text-primary">Shifts</span>
          </span>
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-5">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={`text-sm font-medium transition-colors ${
                isActive(l.to) ? 'text-primary' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {l.label}
            </Link>
          ))}
          <a
            href="https://buildmychecks.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors flex items-center gap-1"
          >
            BuildMyChecks
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
          </a>
          <LanguageSwitcher variant="light" />
          <Link to="/login" className="text-green-600 hover:text-green-700 text-sm font-semibold">{t('publicNav.login', 'Login')}</Link>
          <Link
            to="/register"
            className="bg-primary text-white px-5 py-2 rounded-lg text-sm font-semibold hover:bg-primary-dark transition-colors"
          >
            {t('publicNav.startTrial', 'Start Free Trial')}
          </Link>
        </div>

        {/* Mobile hamburger */}
        <button
          className="md:hidden p-2 text-gray-600 hover:text-gray-900"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-gray-100 bg-white px-4 pb-4 space-y-2">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setMobileOpen(false)}
              className={`block py-2 text-sm font-medium ${
                isActive(l.to) ? 'text-primary' : 'text-gray-600'
              }`}
            >
              {l.label}
            </Link>
          ))}
          <a
            href="https://buildmychecks.com"
            target="_blank"
            rel="noopener noreferrer"
            className="block py-2 text-sm font-medium text-gray-600 hover:text-gray-900"
          >
            BuildMyChecks ↗
          </a>
          <div className="py-2">
            <LanguageSwitcher variant="light" />
          </div>
          <Link
            to="/login"
            onClick={() => setMobileOpen(false)}
            className="block py-2 text-sm font-semibold text-green-600"
          >
            {t('publicNav.login', 'Login')}
          </Link>
          <Link
            to="/register"
            onClick={() => setMobileOpen(false)}
            className="block bg-primary text-white text-center px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-primary-dark transition-colors"
          >
            {t('publicNav.startTrial', 'Start Free Trial')}
          </Link>
        </div>
      )}
    </nav>
  );
}
